import { AfterViewInit, Component, EventEmitter, forwardRef, Input, Output, inject, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, FormGroup, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatDialog } from '@angular/material/dialog';
import { Advertiser, CoinReward, ProductReward, RewardData, StampReward } from 'src/app/api/api-campaigns.service.generated';
import { AdvertiserCampaignRewardEditComponent } from '../../advertiser-campaign-reward-edit/advertiser-campaign-reward-edit.component';
import { PhotoComponent, ButtonComponent } from 'wecheer-common';

@Component({
  selector: 'app-reward-data-edit',
  templateUrl: './reward-data-edit.component.html',
  styleUrls: ['./reward-data-edit.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => RewardDataEditComponent),
    multi: true
  }],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    FlexLayoutModule,
    PhotoComponent,
    ButtonComponent
  ]
})
export class RewardDataEditComponent implements ControlValueAccessor, AfterViewInit {
  private dialog = inject(MatDialog);


  @Input() advertiser: Advertiser;
  @Input() rewardProductsOptions: ProductReward[] = [];
  @Input() label: string;
  @Input() allowCoins: boolean = false;
  @Input() allowStamps: boolean = false;
  @Input() allowPoints: boolean = false;
  @Input() hasToggle: boolean = false;

  @Output() onDataEndabledChanged: EventEmitter<boolean> = new EventEmitter();
  dataEnabled: boolean = true;
  cachedData: any;

  grp = new FormGroup({
    dataEnabled: new FormControl(),
    productReward: new FormControl(),
    coinReward: new FormControl(null, Validators.min(0)),
    stampReward: new FormControl(),
  });


  coinRewardOptions = [
    new CoinReward({ coins: 1 }),
    new CoinReward({ coins: 2 }),
    new CoinReward({ coins: 3 }),
    new CoinReward({ coins: 5 }),
    new CoinReward({ coins: 8 }),
    new CoinReward({ coins: 10 }),
    new CoinReward({ coins: 20 }),
    new CoinReward({ coins: 50 }),
    new CoinReward({ coins: 100 })
  ];

  stampRewardOptions = [
    new StampReward({ stamps: 1 }),
    new StampReward({ stamps: 2 }),
    new StampReward({ stamps: 3 }),
    new StampReward({ stamps: 5 }),
    new StampReward({ stamps: 8 }),
    new StampReward({ stamps: 10 }),
    new StampReward({ stamps: 20 }),
    new StampReward({ stamps: 50 }),
    new StampReward({ stamps: 100 })
  ];


  /// ControlValueAccessor-------------------
  @Input('value') _value;
  onChange: any = () => { };
  onTouched: any = () => { };

  get value(): RewardData {
    return this._value;
  }
  set value(data: RewardData) {
    this._value = data;
    this.grp.patchValue({
      ...data,
      coinReward: data?.coinReward?.coins,
      dataEnabled: this.dataEnabled
    });

    this.onChange(data);
    this.onTouched();
    // this.checkEnabledData();
  }

  ngAfterViewInit(): void {
    this.cachedData = this._value;
    this.checkEnabledData();
  }

  checkEnabledData() {
    this.dataEnabled = !!(this.grp.get('coinReward').value || this.grp.get('productReward').value || this.grp.get('stampReward').value);
  }

  onToggleChange(ev) {
    this.onDataEndabledChanged.emit(ev.checked);
    this.dataEnabled = ev.checked;
    if (!ev.checked) {
      this.grp.patchValue({
        stampReward: undefined,
        productReward: undefined,
        coinReward: undefined,
        dataEnabled: this.dataEnabled
      })
    } else {
      this.grp.patchValue({
        ...this.cachedData
      })
    }

    this.value = this.getFormData();
  }
  writeValue(obj: any): void {
    if (obj)
      this.value = obj;
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(_isDisabled: boolean): void {
  }

  getFormData(): RewardData {
    const f = this.grp.value;
    const hasProduct = f.productReward && f.productReward.rewardTitle;
    const hasCoins = f.coinReward && f.coinReward.coins;
    const hasInputCoins = f.coinReward;
    const hasStamps = f.stampReward && f.stampReward.stamps;
    if (!hasProduct && !hasCoins && !hasStamps && !hasInputCoins) return undefined;

    return new RewardData({
      productReward: hasProduct ? new ProductReward(f.productReward) : undefined,
      coinReward: hasCoins ? new CoinReward(f.coinReward) : hasInputCoins ? new CoinReward({ coins: f.coinReward }) : undefined,
      stampReward: hasStamps ? new StampReward(f.stampReward) : undefined,
    });
  }

  // complete reward
  async onAddRewardProduct() {
    const productReward = await AdvertiserCampaignRewardEditComponent.showDialog(this.dialog, { advertiser: this.advertiser });
    if (!productReward) return;

    productReward.uploadKey = productReward.uploadKey;                     // this flag must create the product reward the the campaing is created

    this.rewardProductsOptions = [
      productReward,                    // push at 0 index
      ...this.rewardProductsOptions
    ];

    this.grp.patchValue({ productReward: productReward });
    this.value = this.getFormData();
  }
  async onRemoveRewardProduct() {
    this.dataEnabled = false;
    this.grp.patchValue({ productReward: undefined, dataEnabled: false });

    if (this.allowPoints) {
      this.grp.patchValue({ coinReward: undefined });
    }

    this.value = this.getFormData();
    this.checkEnabledData();
  }

  onProductSelected(_e) {
    this.value = this.getFormData();
    this.checkEnabledData();
  }
  onCoinSelected(_e) {
    this.value = this.getFormData();
    this.checkEnabledData();
  }
  onStampSelected(_e) {
    this.value = this.getFormData();
    this.checkEnabledData();
  }
  onPointChange(_e) {
    this.value = this.getFormData();
    this.checkEnabledData();
  }

  compareRewardData: ((a: RewardData, b: RewardData) => boolean) | null = (a: RewardData, b: RewardData) => a && b && a.productReward.productSlug === b.productReward.productSlug;
  compareCoinRewardFn: ((a: CoinReward, b: CoinReward) => boolean) | null = (a: any, b: any) => a && b && a.coins === b.coins;
  compareProductRewardFn: ((a: ProductReward, b: ProductReward) => boolean) | null = (a: ProductReward, b: ProductReward) => a && b && a.productSlug === b.productSlug;
  compareStampRewardFn: ((a: StampReward, b: StampReward) => boolean) | null = (a: any, b: any) => a && b && a.stamps === b.stamps;
}

// convers js object to RewardData adding toJson() method so it can be serialized when calling api
export function formToRewardData(formRewardData: any): RewardData {

  const f = formRewardData as RewardData;
  if (!f) return undefined;
  return new RewardData({
    productReward: (f.productReward && f.productReward.rewardTitle) ? new ProductReward({ ...f.productReward }) : undefined,
    coinReward: (f.coinReward && f.coinReward.coins) ? new CoinReward({ ...f.coinReward }) : undefined,
    stampReward: (f.stampReward && f.stampReward.stamps) ? new StampReward({ ...f.stampReward }) : undefined,
  });

}
